import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { allIllustrations } from './foundation';
import { FactoryProvider, inject, InjectionToken, provideAppInitializer, ValueProvider } from '@angular/core';
import { CUSTOM_DATE_FORMATS } from './form/mm-datepicker/custom-date-adapter/custom-date-adapter';
import { provideDateFnsAdapter } from '@angular/material-date-fns-adapter';
import { allIcons } from './foundation/icons/icons';
import { SidebarState } from './menu';
import { provideEnvironmentNgxMask } from 'ngx-mask';

export type SidebarCookieState = {
  key: string;
  default: SidebarState;
  domain: string;
};

export type DesignSystemConfig = {
  fileUploadUrl: string;
  sidebar: SidebarCookieState;
  assetsUrl: string;
  /** éviter l'utilisation de breakpoints (hormis dans play) */
  breakpoints?: BreakpointsConfig;
};

/**
 * <strong>Ne pas utiliser cette configuration</strong>.
 *
 * Les breakpoints ne sont pas vraiment configurables pour le moment (voire ne le seront jamais).
 *
 * Il s'agit d'une implémentation partielle dédiée aux beaux yeux de l'application play (qui n'est
 * pas responsive et pour lequel on paramètre le breakpoint desktop à 1px).
 *
 * BreakpointsConfig est uniquement utilisé dans les fichiers '.ts',
 * il n'est pas utilisés dans les scss / html (tailwind).
 */
export type BreakpointsConfig = {
  lg: number;
};

export const MM_DS_CONFIG = new InjectionToken<Partial<DesignSystemConfig>>('mm-ds.config');

export const MM_DS_CONFIG_BREAKPOINTS = new InjectionToken<BreakpointsConfig>('mm-ds.config.breakpoints');

/**
 * Registers our svg illustrations to use them with mat-icon (needed in icon-button)
 */
function initializeIcons(iconRegistry: MatIconRegistry, sanitizer: DomSanitizer, assetsUrl: string) {
  // illustrations
  for (const [name, url] of Object.entries(allIllustrations)) {
    iconRegistry.addSvgIconInNamespace('ds', name, sanitizer.bypassSecurityTrustResourceUrl(`${assetsUrl}${url}`));
  }
  // icons
  for (const [name, url] of Object.entries(allIcons)) {
    iconRegistry.addSvgIconInNamespace('ds', name, sanitizer.bypassSecurityTrustResourceUrl(`${assetsUrl}${url}`));
  }
}

/**
 * Provides the Design System configuration and initializes required components
 */
export function provideDsConfig(config?: Partial<DesignSystemConfig>) {
  return [
    {
      provide: MM_DS_CONFIG,
      useValue: config ?? {},
    } as ValueProvider,
    {
      provide: MM_DS_CONFIG_BREAKPOINTS,
      useFactory: (c: Partial<DesignSystemConfig>) => c.breakpoints ?? { lg: 1024 },
      deps: [MM_DS_CONFIG],
    } as FactoryProvider,
    provideEnvironmentNgxMask(),
    provideAppInitializer(() => {
      const iconRegistry = inject(MatIconRegistry);
      const sanitizer = inject(DomSanitizer);
      const dsConfig = inject(MM_DS_CONFIG);
      initializeIcons(iconRegistry, sanitizer, dsConfig.assetsUrl ?? '');
    }),
  ];
}

/**
 * Provides the custom date fns adapter for angular material used by the design system
 */
export function provideCustomDateFnsAdapter() {
  return [provideDateFnsAdapter(CUSTOM_DATE_FORMATS)];
}
